import type { Locale } from 'date-fns';
import type { BookingContextValue } from '@features/booking/state/types';
import type { Restaurant } from '@shared/types/restaurant';
import type { AuthState } from '@core/auth/types';
import type { BookingStepId } from '@features/booking/flow';
import { calculateReservationSource, getUtmParams, getCapturedReferrer } from '@shared/utils/reservationSource';
import { captureMissingReservationId } from '@core/sentry';
import type { MissingReservationIdContext } from '@core/sentry';
import type { BillPersonalBusiness } from '@features/booking/api/reservations';
import { BILL_TYPE_CONTRACT } from '@features/booking/utils/billingReceiptLabels';

/**
 * The fee breakdown structure used by deposit/no-show payloads. Extracted
 * here to keep this module independent of the payment-fee hook.
 */
export interface FeeBreakdown {
  salesTaxPercent: number;
  gratuityPercent: number;
}

/**
 * Conversion event payload — mirrors the shape ConfirmationPage.fireConversion
 * passed to useAnalytics().trackConversion.
 *
 * Note: uniqueId is optional because some API responses omit it; the original
 * handleConfirm passed `response.uniqueId` through verbatim (undefined ok).
 * Coercing to '' would change downstream attribution semantics.
 */
export interface ConversionPayload {
  reservationId: string;
  uniqueId: string | undefined;
  diners: number;
  date: string;
  time: string;
  guaranteeType: GuaranteeTypeForConversion;
  fee: number;
  currency: string;
  isWaitingList: boolean;
}

/**
 * The literal guarantee-type strings passed to trackConversion. Includes
 * 'early_access' which is set by the early-access path but is not a real
 * booking.guaranteeType value.
 */
export type GuaranteeTypeForConversion =
  | 'traditional'
  | 'deposit'
  | 'no_show'
  | 'early_access';

/**
 * The context object passed through every commit path. Mirrors the hook
 * signature in docs/plans/preflight/preflight-coupling.md §10.
 *
 * Every field is supplied by the caller (the React hook) — no internal hook
 * calls inside commitReservation, so the function can be tested with mocks
 * and called from any context.
 */
export interface CommitContext {
  booking: BookingContextValue;
  restaurant: Restaurant;
  authState: AuthState;
  feeBreakdown: FeeBreakdown;
  currentLanguage: string;
  dateLocale: Locale;
  // Side-effect services (provided by hook so they pick up provider context)
  trackConversion: (payload: ConversionPayload) => void;
  invalidateTimeSlots: () => void;
  // Navigation (from useBookingFlow)
  goToNext: () => void;
  goToStep: (id: BookingStepId) => void;
  /**
   * Fired by the path modules immediately before `handle3dsChallenge` is
   * awaited (Stripe or MercadoPago). Lets the hook flip a UI flag so the
   * page can render <ThreeDSChallengeOverlay open={true} />.
   *
   * Optional — paths that never invoke 3DS (early access, traditional) don't
   * call it. Hook may pass `undefined` if the page doesn't host the overlay.
   */
  on3dsStart?: () => void;
  /**
   * Fired in the `finally` after handle3dsChallenge resolves OR rejects. The
   * hook flips the UI flag back so the overlay unmounts before the page
   * navigates (success) or renders the inline error (failure).
   */
  on3dsEnd?: () => void;
}

/**
 * Derived computed once at commit time and shared across paths. Avoids
 * re-deriving on every branch.
 */
export interface CommitDerived {
  isWaitingList: boolean;
  bypassesGuarantee: boolean;
  effectiveGuaranteeType: 'traditional' | 'deposit' | 'no_show';
  /** Pre-resolved guest info — agent uses booking.* directly, non-agent uses auth fields with booking.* fallback. */
  guestName: string;
  guestEmail: string;
  guestPhone: string;
  /** Display currency for trackConversion. */
  displayCurrency: string;
}

/**
 * Mutable carrier passed into commitDepositMercadoPago so the MP-only
 * `mpFlow3dsLoaded` flag survives even when the function throws.
 *
 * The original handleConfirm declared this flag at the outer function scope
 * (line 279 of the pre-extraction ConfirmationPage); the outer catch read
 * its post-assignment value to gate setRequireReload. Post-extraction,
 * commitDepositMercadoPago is a separate function — a local `let` inside
 * it is invisible to the caller on throw. This box restores the invariant:
 * the function writes to box.mpFlow3dsLoaded right where it would set the
 * local var, and the outer catch reads it whether the function returned
 * normally or threw.
 */
export interface MpFlowFlagBox {
  mpFlow3dsLoaded: boolean;
}

/**
 * Resolve the display currency from the restaurant config.
 * Used both by the commit pipeline (for conversion tracking) and by the
 * confirmation page render. Single source of truth so they can't drift.
 */
export function getDisplayCurrency(restaurant: Restaurant): string {
  return restaurant.reservationGuaranteeCurrency ?? restaurant.currency ?? 'USD';
}

/**
 * The base reservation fields that buildBasePayload emits. Compatible with
 * every concrete API payload type (TraditionalGrabPayload, DepositGrabPayload,
 * DepositGrabMercadoPagoPayload, EarlyAccessGrabPayload, plus their WL
 * variants which differ on `date`/`menu` shape).
 *
 * `date` and `menu` are union-typed because WL endpoints take arrays while
 * standard endpoints take scalars. Path modules narrow at assembly time.
 */
export interface BaseReservationFields {
  restaurant: number;
  date: string | string[];
  time: string;
  partySize: number;
  menu: number | number[];
  area: number;
  source: string;
  referrer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  pairings: { id: number; quantity: number }[];
  veggie: boolean;
  howManyVeggie: number;
  kids: boolean;
  howManyKids: number;
  allergies: string;
  pets: boolean;
  accesibility: boolean;  // legacy spelling (single 's')
  gratuity: number;
  giftCardToken: string | null;
  suggestedFromWaitingListAlternatives: boolean;
  // Conditional fields (only present when triggered)
  serviceType?: string;
  rescheduleId?: number;
  meitreNetwork?: boolean;
  meitreNetworkMenuId?: number;
  agentReservation?: boolean;
  guestName?: string;
  guestEmail?: string;
  guestPhone?: string;
  requestGuarantee?: boolean;
}

/**
 * Deposit-specific fields that augment the base for /deposit-grab-stripe and
 * /deposit-grab-mercadopago payloads.
 */
export interface DepositFields {
  salesTax: number;
  serviceIncluded: boolean;
  tipChargePercent: string;
  payWithCard: boolean;
  agentReservation: boolean;
  partySizeType: 'normal';
  AMEXChange?: boolean;
  billingAddress?: string;
  identityDocument?: string;
  billPersonalBusiness?: BillPersonalBusiness;
}

/**
 * Build the base payload shared across all reservation API paths.
 *
 * Mirrors ConfirmationPage handleConfirm verbatim — including:
 * - `accesibility` (legacy spelling, single 's') required by backend contract
 * - WL fields only in WL flow
 * - agent fields only when isAgentReservation
 * - rescheduleId/meitreNetwork conditional spreads
 *
 * Returns BaseReservationFields — the typed shape every path module can
 * spread into its specific API payload.
 */
export function buildBasePayload(ctx: CommitContext, derived: CommitDerived): BaseReservationFields {
  const { booking, restaurant } = ctx;
  const { isWaitingList, guestName, guestEmail, guestPhone } = derived;

  return {
    restaurant: restaurant.id,
    date: isWaitingList
      ? booking.wlDates
      : booking.selectedDate!.split('T')[0],
    time: isWaitingList
      ? (booking.wlPreferredTime ?? '-1')
      : (booking.selectedTime ?? ''),
    partySize: booking.diners,
    menu: isWaitingList
      ? booking.wlMenus
      : (Number(booking.selectedExperience?.id) || 0),
    area: Number(booking.selectedArea?.id) || 0,
    source: calculateReservationSource(),
    referrer: getCapturedReferrer() || undefined,
    ...getUtmParams(),
    pairings: booking.selectedPairings.map((p) => ({
      id: p.id,
      quantity: p.quantity,
    })),
    veggie: booking.veggie,
    howManyVeggie: booking.howManyVeggie,
    kids: booking.kids,
    howManyKids: booking.howManyKids,
    allergies: booking.allergies,
    pets: booking.pets,
    accesibility: booking.accessibility,
    gratuity: isWaitingList ? 0 : booking.feeGratuity,
    giftCardToken: booking.giftCardToken,
    suggestedFromWaitingListAlternatives: booking.suggestedFromWaitingListAlternatives,
    ...(isWaitingList ? { serviceType: booking.wlServiceType ?? '-1' } : {}),
    ...(booking.rescheduleId ? { rescheduleId: booking.rescheduleId } : {}),
    ...(booking.isStaffBooking ? { meitreNetwork: true } : {}),
    ...(booking.meitreNetworkMenuId ? { meitreNetworkMenuId: booking.meitreNetworkMenuId } : {}),
    ...(booking.isAgentReservation ? {
      agentReservation: true,
      guestName,
      guestEmail,
      guestPhone,
      requestGuarantee: booking.requestGuarantee,
    } : {}),
  };
}

/**
 * Build the deposit-specific fields used by deposit/no-show payloads.
 */
export function buildDepositFields(ctx: CommitContext): DepositFields {
  const { booking, feeBreakdown } = ctx;
  return {
    salesTax: feeBreakdown.salesTaxPercent,
    serviceIncluded: true,
    tipChargePercent: String(feeBreakdown.gratuityPercent),
    payWithCard: false,
    agentReservation: booking.isAgentReservation,
    partySizeType: 'normal',
    ...(booking.amexChange ? { AMEXChange: true } : {}),
    ...buildBillingFields(ctx),
  };
}

/**
 * Build the billing fields conditionally included in deposit payloads.
 * Address, identity document, and the boleta/factura BillPersonalBusiness split.
 */
export function buildBillingFields(ctx: CommitContext): Partial<DepositFields> {
  const { booking } = ctx;
  const billingFields: Partial<DepositFields> = {};
  if (booking.billingAddress) {
    billingFields.billingAddress = booking.billingAddress;
  }
  if (booking.identityDocument) {
    billingFields.identityDocument = booking.identityDocument;
  }
  if (booking.billType && hasReceiptContent(booking)) {
    const bpb: BillPersonalBusiness = { billType: booking.billType };
    if (booking.billType === BILL_TYPE_CONTRACT.personal) {
      bpb.billPersonalName = booking.billPersonalName;
      bpb.billPersonalDocument = booking.billPersonalDocument;
      bpb.billPersonalAddress = booking.billPersonalAddress;
    } else {
      bpb.billBusinessName = booking.billBusinessName;
      bpb.billBusinessDocument = booking.billBusinessDocument;
      bpb.billBusinessAddress = booking.billBusinessAddress;
    }
    billingFields.billPersonalBusiness = bpb;
  }
  return billingFields;
}

/**
 * billType alone does not mean the guest filled the fiscal form in: it survives
 * in booking state after the block stops being rendered. The backend flattens
 * whatever arrives into a free-text column, so sending a bare type would store
 * "factura   " against the reservation with nothing to show for it.
 */
function hasReceiptContent(booking: CommitContext['booking']): boolean {
  const fields =
    booking.billType === BILL_TYPE_CONTRACT.personal
      ? [booking.billPersonalName, booking.billPersonalDocument, booking.billPersonalAddress]
      : [booking.billBusinessName, booking.billBusinessDocument, booking.billBusinessAddress];

  return fields.some((field) => Boolean(field?.trim()));
}

/**
 * Fire the conversion event with the path-specific guaranteeType.
 * Centralizes the conversion-payload shape so per-path modules don't drift.
 */
export function fireConversion(
  ctx: CommitContext,
  derived: CommitDerived,
  reservationId: string,
  uniqueId: string | undefined,
  guaranteeType: GuaranteeTypeForConversion,
): void {
  const { booking, trackConversion } = ctx;
  trackConversion({
    reservationId,
    uniqueId,
    diners: booking.diners,
    date: booking.selectedDate || '',
    time: booking.selectedTime || '',
    guaranteeType,
    fee: booking.feeDeposit,
    currency: derived.displayCurrency,
    isWaitingList: derived.isWaitingList,
  });
}

/**
 * Thrown when a reservation/waiting-list endpoint returns success (2xx) but the
 * response carries no usable reservationId. The commit pipeline's outer catch
 * maps this to a generic error and keeps the user on the confirmation page
 * instead of advancing to /booking-final. Sentry is captured at the throw site
 * (captureMissingReservationId) — capturePaymentError deliberately skips this
 * error class to avoid a duplicate issue.
 */
export class MissingReservationIdError extends Error {
  constructor(public readonly endpoint: string) {
    super(`Reservation endpoint "${endpoint}" returned success without a reservationId`);
    this.name = 'MissingReservationIdError';
  }
}

/**
 * Guard a reservation-creation response before treating the commit as a success.
 *
 * The backend has been observed to return 2xx with no usable reservationId —
 * absent, empty string, or the literal strings 'undefined'/'null' (the latter
 * via String(undefined) in handle3dsChallenge's confirm step). Advancing to
 * /booking-final in that state shows a "confirmed" screen for a reservation that
 * does not exist. We log to Sentry with a dedicated fingerprint and throw so the
 * commit pipeline surfaces an error instead of a phantom confirmation.
 *
 * Returns the validated id (narrowed to string) for the happy path.
 */
export function assertReservationId(
  reservationId: string | undefined,
  context: MissingReservationIdContext,
): string {
  if (!reservationId || reservationId === 'undefined' || reservationId === 'null') {
    captureMissingReservationId(context);
    throw new MissingReservationIdError(context.endpoint);
  }
  return reservationId;
}

/**
 * State mutation + navigation sequence fired on every successful commit.
 *
 * Order is invariant: setReservationResult → setGuestInfo → fireConversion
 * → invalidateTimeSlots → goToStep('BOOKING_FINAL'). Phase 1 extraction
 * must preserve this.
 *
 * Why `goToStep('BOOKING_FINAL')` not `goToNext()`: a successful commit
 * ALWAYS lands on /booking-final regardless of source step or step-set
 * mutation that happened during commit. `goToNext()` reads through
 * `useBookingFlow`'s navStateRef which gets re-derived from booking state;
 * if the commit-host page mutated something that flips its own step's
 * `isRequired` (e.g. GUARANTEE setting cardToken filters itself out),
 * `currentStepIndex` becomes -1, `getNextStep()` returns null, and
 * `goToNext()` silently no-ops. Bug reproduced 2026-05-21 — Stripe form
 * as commit host succeeded the POST but stayed on /payment. Explicit
 * destination avoids the whole state-inference hazard.
 */
export function persistSuccessAndAdvance(
  ctx: CommitContext,
  derived: CommitDerived,
  reservationId: string,
  uniqueId: string | undefined,
  guaranteeType: GuaranteeTypeForConversion,
): void {
  ctx.booking.setReservationResult(reservationId, uniqueId);
  ctx.booking.setGuestInfo(derived.guestName, derived.guestEmail, derived.guestPhone);
  fireConversion(ctx, derived, reservationId, uniqueId, guaranteeType);
  ctx.invalidateTimeSlots();
  ctx.goToStep('BOOKING_FINAL');
}
